import React, { useEffect, useState } from "react";
import MUIDataTable from "mui-datatables";
import { createMuiTheme, MuiThemeProvider } from "@material-ui/core/styles";
import {
  Button,
  CircularProgress,
  Fab,
  LinearProgress,
  TextField,
  Typography
} from "@material-ui/core";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import EditIcon from "@material-ui/icons/Edit";
import ViewIcon from "@material-ui/icons/Visibility";
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";
import ErrorOutlineIcon from "@material-ui/icons/ErrorOutline";
import HourglassEmptyIcon from "@material-ui/icons/HourglassEmpty";
import ReportProblemOutlinedIcon from "@material-ui/icons/ReportProblemOutlined";
import IconButton from "@material-ui/core/IconButton";
import Tooltip from "@material-ui/core/Tooltip";
import FilterIcon from "@material-ui/icons/FilterList";

import AddIcon from "@material-ui/icons/Add";
import InvoiceIcon from "@material-ui/icons/Receipt";
import axios from "axios";
import api from "../common/api";
import Snackbar from "@material-ui/core/Snackbar";
import MuiAlert from "@material-ui/lab/Alert";

function Alert(props) {
  return <MuiAlert elevation={6} variant="filled" {...props} />;
}

const STATUS_STYLES = {
  pending: {
    color: "#9a6700",
    background: "#fff8e6",
    border: "#f0d78c",
    Icon: HourglassEmptyIcon
  },
  generated: {
    color: "#1b7a3d",
    background: "#eaf7ef",
    border: "#9fd4b0",
    Icon: CheckCircleOutlineIcon
  },
  "sap error": {
    color: "#b71c1c",
    background: "#fdecea",
    border: "#f5b7b1",
    Icon: ErrorOutlineIcon
  },
  "arca error": {
    color: "#b71c1c",
    background: "#fdecea",
    border: "#f5b7b1",
    Icon: ReportProblemOutlinedIcon
  }
};

const DEFAULT_STATUS_STYLE = {
  color: "#455a64",
  background: "#f5f7f9",
  border: "#cfd8dc",
  Icon: InvoiceIcon
};

const getStatusStyle = value => {
  const key = String(value || "")
    .toLowerCase()
    .trim();
  return STATUS_STYLES[key] || DEFAULT_STATUS_STYLE;
};

const normalizeStatusLabels = raw => {
  if (!raw) return [];

  if (Array.isArray(raw)) {
    return raw
      .map(item => {
        if (typeof item === "string" || typeof item === "number") {
          const value = String(item);
          return { value: value, label: value };
        }
        if (!item || typeof item !== "object") return null;

        const value =
          item.value != null
            ? item.value
            : item.key != null
            ? item.key
            : item.status != null
            ? item.status
            : item.id != null
            ? item.id
            : item.name != null
            ? item.name
            : null;
        const label =
          item.label != null
            ? item.label
            : item.title != null
            ? item.title
            : item.name != null
            ? item.name
            : value;

        if (value == null || value === "") return null;
        return { value: String(value), label: String(label) };
      })
      .filter(Boolean);
  }

  if (typeof raw === "object") {
    return Object.keys(raw).map(value => {
      const entry = raw[value];
      const label =
        typeof entry === "string"
          ? entry
          : entry && (entry.label || entry.title || entry.name)
          ? entry.label || entry.title || entry.name
          : value;
      return { value: String(value), label: String(label) };
    });
  }

  return [];
};

const buildStatusLookup = statuses => {
  const byValue = {};
  const byTitle = {};

  (statuses || []).forEach(status => {
    if (!status || typeof status !== "object") return;

    const statusKey =
      status.status != null
        ? status.status
        : status.value != null
        ? status.value
        : status.key != null
        ? status.key
        : status.id != null
        ? status.id
        : null;
    const statusTitle =
      status.title != null
        ? status.title
        : status.label != null
        ? status.label
        : status.name != null
        ? status.name
        : null;

    if (statusKey != null && String(statusKey) !== "") {
      byValue[String(statusKey).toLowerCase()] = status;
    }
    if (statusTitle != null && String(statusTitle) !== "") {
      byTitle[String(statusTitle).toLowerCase()] = status;
    }
  });

  return { byValue: byValue, byTitle: byTitle };
};

const Invoices = ({ client_id }) => {
  const [data, setData] = useState([]);
  const [snackBar, setSnackBar] = useState({
    open: false,
    message: "",
    severity: "success"
  });
  const [sendingInvoices, setSendingInvoices] = useState(false);
  const [page, setPage] = useState(0);
  const [count, setCount] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(100);
  const [filters, setFilters] = useState([
    [],
    [],
    [],
    [],
    [],
    [],
    !client_id ? ["sap error", "pending"] : [],
    [],
    [],
    [],
    [],
    []
  ]);
  const [orderColumn, setOrderColumn] = useState("");
  const [orderOrder, setOrderOrder] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [invoiceDateRange, setInvoiceDateRange] = useState({
    from: "",
    to: ""
  });
  const [invoicesToSend, setInvoicesToSend] = useState([]);
  const [statusIndicators, setStatusIndicators] = useState([]);
  const [statusLoading, setStatusLoading] = useState(false);
  const [pdfDialog, setPdfDialog] = useState({
    open: false,
    url: null,
    loading: false,
    invoiceId: null
  });

  const getMuiTheme = () =>
    createMuiTheme({
      overrides: {
        MuiTableCell: {
          root: {
            padding: "0.25rem",
            fontSize: "0.8rem",
            maxWidth: "10rem"
          }
        }
      }
    });

  const fetchData = async (page, filterList, column, order, q_rows) => {
    setIsLoading(true);
    let url = api.invoices().index(page + 1, "", "", column, order, q_rows);
    if (filterList[1].length !== 0) {
      url += `&number=${filterList[1].join(",")}`;
    }
    if (filterList[2].length !== 0) {
      url += `&sap_code=${filterList[2].join(",")}`;
    }
    if (filterList[3].length !== 0) {
      const [from, to] = filterList[3];
      if (from) url += `&invoice_date_from=${from}`;
      if (to) url += `&invoice_date_to=${to}`;
    }
    if (filterList[4].length !== 0) {
      url += `&client.business_name=${filterList[4].join(",")}`;
    }
    if (filterList[6].length !== 0) {
      url += `&status=${filterList[6].join(",")}`;
    }
    if (filterList[11].length !== 0) {
      url += `&client_id=${filterList[11].join(",")}`;
    }
    if (client_id) {
      url += `&client_id=${client_id}`;
    }
    if (filterList[7].length !== 0) {
      url += `&point_of_sales=${filterList[7].join(",")}`;
    }
    if (filterList[8].length !== 0) {
      url += `&level_1=${filterList[8].join(",")}`;
    }
    if (filterList[9].length !== 0) {
      url += `&level_2=${filterList[9].join(",")}`;
    }

    const token = localStorage.getItem("userToken");
    try {
      const res = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setData(res.data.data);
      setCount(res.data.meta.total);
      setRowsPerPage(res.data.meta.per_page);
    } catch (err) {
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchStatus = async () => {
    const token = localStorage.getItem("userToken");
    const headers = { Authorization: `Bearer ${token}` };
    setStatusLoading(true);
    try {
      const [labelsRes, statusRes] = await Promise.all([
        axios.get(api.invoices().statusLabels(), { headers }),
        axios.get(api.invoices().status(), { headers })
      ]);

      const labels = normalizeStatusLabels(
        labelsRes.data && labelsRes.data.status_labels
      );

      const statusData = statusRes.data || {};
      const statuses = Array.isArray(statusData.statuses)
        ? statusData.statuses
        : Array.isArray(statusData.data)
        ? statusData.data
        : Array.isArray(statusRes.data)
        ? statusRes.data
        : [];

      const lookup = buildStatusLookup(statuses);

      const indicators = labels.map(label => {
        const valueKey = String(label.value).toLowerCase();
        const titleKey = String(label.label).toLowerCase();
        const match =
          lookup.byValue[valueKey] ||
          lookup.byTitle[titleKey] ||
          lookup.byTitle[valueKey] ||
          lookup.byValue[titleKey] ||
          null;

        return {
          value: label.value,
          title: label.label,
          subtitle: (match && match.subtitle) || "",
          count: match && match.count != null ? Number(match.count) : 0
        };
      });

      setStatusIndicators(indicators);
    } catch (err) {
      console.error(err);
    } finally {
      setStatusLoading(false);
    }
  };

  const handleStatusFilter = statusValue => {
    const nextFilters = [...filters];
    const current = nextFilters[6] || [];
    const isActive =
      current.length === 1 && String(current[0]) === String(statusValue);
    nextFilters[6] = isActive ? [] : [statusValue];
    setPage(0);
    setFilters(nextFilters);
  };

  const handleOpenPdf = async invoiceId => {
    const token = localStorage.getItem("userToken");
    setPdfDialog({
      open: true,
      url: null,
      loading: true,
      invoiceId
    });
    try {
      const res = await axios.get(api.invoices().exportPdf(invoiceId), {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob"
      });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      setPdfDialog({
        open: true,
        url,
        loading: false,
        invoiceId
      });
    } catch (err) {
      console.error(err);
      setPdfDialog({
        open: false,
        url: null,
        loading: false,
        invoiceId: null
      });
      setSnackBar({
        open: true,
        message: "Error al cargar el PDF de la factura",
        severity: "error"
      });
    }
  };

  const handleClosePdf = () => {
    if (pdfDialog.url) {
      URL.revokeObjectURL(pdfDialog.url);
    }
    setPdfDialog({
      open: false,
      url: null,
      loading: false,
      invoiceId: null
    });
  };

  useEffect(() => {
    fetchData(page, filters, orderColumn, orderOrder, rowsPerPage);
  }, [page, filters, orderColumn, orderOrder, rowsPerPage]);

  useEffect(() => {
    fetchStatus();
  }, []);

  const columns = [
    {
      name: "id",
      label: "ID",
      options: {
        filter: false,
        sort: true,
        sortDirection: orderColumn === "id" ? orderOrder : null
      }
    },
    {
      name: "number",
      label: "Número",
      options: {
        filter: true,
        filterType: "textField",
        sort: true,
        sortDirection: orderColumn === "invoice_number" ? orderOrder : null
      }
    },
    {
      name: "sap_code",
      label: "Código SAP",
      options: {
        filter: true,
        filterType: "textField",
        sort: false
      }
    },
    {
      name: "invoice_date",
      label: "Fecha factura",
      options: {
        filter: true,
        filterType: "custom",
        filterOptions: {
          display: (filterList, onChange, index, column) => (
            <div>
              <TextField
                label="Fecha desde"
                InputLabelProps={{
                  shrink: true
                }}
                type="date"
                value={filterList[index][0] || ""}
                onChange={event => {
                  filterList[index][0] = event.target.value;
                  onChange(filterList[index], index, column);
                }}
                style={{ width: "45%" }}
              />
              <TextField
                label="Fecha hasta"
                type="date"
                InputLabelProps={{
                  shrink: true
                }}
                value={filterList[index][1] || ""}
                onChange={event => {
                  filterList[index][1] = event.target.value;
                  onChange(filterList[index], index, column);
                }}
                style={{ width: "45%" }}
              />
            </div>
          )
        },
        customFilterListRender: v => {
          if (!v || v.length === 0) return null;
          const [from, to] = v;
          return [`Desde: ${from || "..."}`, `Hasta: ${to || "..."}`];
        },
        onFilterChipClose: () => {
          setInvoiceDateRange({ from: "", to: "" });
          const newFilterList = [...filters];
          newFilterList[3] = [];
          setFilters(newFilterList);
        },
        sort: true,
        sortDirection: orderColumn === "date" ? orderOrder : null
      }
    },
    {
      name: "client.business_name",
      label: "Cliente",
      options: {
        filter: true,
        filterType: "textField",
        sort: false
      }
    },
    {
      name: "total",
      label: "Total",
      options: {
        filter: false,
        sort: true,
        sortDirection: orderColumn === "total" ? orderOrder : null,
        customBodyRender: value => {
          if (typeof value === "string" && value.includes("$")) {
            return value;
          }
          return new Intl.NumberFormat("es-AR", {
            style: "currency",
            currency: "ARS"
          }).format(value);
        }
      }
    },
    {
      name: "status",
      label: "Estado",
      options: {
        filter: true,
        filterType: "select",
        filterOptions: {
          names:
            statusIndicators.length > 0
              ? statusIndicators.map(s => s.value)
              : ["pending", "sap error", "generated", "arca error"]
        },
        sort: false
      }
    },
    {
      name: "point_of_sales.name",
      label: "Punto de venta",
      options: {
        filter: true,
        filterType: "textField",
        sort: false
      }
    },
    {
      name: "level_1",
      label: "Agrupación",
      options: {
        filter: true,
        filterType: "textField",
        sort: false
      }
    },
    {
      name: "level_2",
      label: "Agrupacion 2",
      options: {
        filter: true,
        filterType: "textField",
        sort: false
      }
    },
    {
      name: "due_date",
      label: "Fecha de vencimiento",
      options: {
        filter: true,
        display: false,
        filterType: "custom",
        filterOptions: {
          display: (filterList, onChange, index, column) => (
            <TextField
              label="Fecha de vencimiento"
              type="date"
              InputLabelProps={{
                shrink: true
              }}
              value={filterList[index] || ""}
              onChange={event => {
                filterList[index] = event.target.value;
                onChange(filterList[index], index, column);
              }}
            />
          )
        },
        customFilterListRender: v => (v ? `Vencimiento: ${v}` : null),
        sort: true,
        sortDirection: orderColumn === "due_date" ? orderOrder : null
      }
    },
    {
      name: "client_id",
      label: "Id Cliente",
      options: {
        filter: true,
        filterType: "textField",
        sort: false,
        display: false
      }
    },
    {
      name: "client.sap_code",
      label: "Cod SAP Cliente",
      options: {
        filter: true,
        filterType: "textField",
        sort: false,
        display: false
      }
    },
    {
      name: "cae",
      label: "CAE",
      options: {
        filter: true,
        filterType: "textField",
        sort: false,
        display: false
      }
    },
    {
      name: "is_fce",
      label: "FCE",
      options: {
        filter: true,
        filterType: "textField",
        sort: false,
        display: false,
        customBodyRender: value => {
          return value ? "Sí" : "No";
        }
      }
    },
    {
      name: "can_edit",
      label: "",
      options: {
        filter: false,
        filterType: "textField",
        sort: false,
        display: false,
        viewColumns: false
      }
    },
    {
      name: "sap_error",
      label: "Error SAP",
      options: {
        filter: false,
        sort: false,
      }
    },
    {
      name: "creator.name",
      label: "Creador",
      options: {
        filter: false,
        sort: false,
        display: false
      }
    },
    {
      name: "Acciones",
      label: "",
      options: {
        filter: false,
        setCellHeaderProps: () => ({ align: "center" }),
        customBodyRender: (value, tableMeta, updateValue) => {
          const rowId = tableMeta.rowData[0];
          return (
            <div className="row" style={{ display: "block" }}>
              <Fab
                color="secondary"
                aria-label="edit"
                variant="extended"
                style={{ boxShadow: "none" }}
                fontSize="small"
                className="MuiButtonBase-root MuiFab-sizeSmall mr-2"
                href={"/invoice/" + rowId}
              >
                <EditIcon fontSize="small" />
              </Fab>
              <Fab
                color="primary"
                aria-label="view-pdf"
                variant="extended"
                style={{ boxShadow: "none", backgroundColor: "green" }}
                fontSize="small"
                className="MuiButtonBase-root MuiFab-sizeSmall mr-2"
                onClick={e => {
                  e.preventDefault();
                  handleOpenPdf(rowId);
                }}
              >
                <ViewIcon fontSize="small" />
              </Fab>
            </div>
          );
        }
      }
    }
  ];

  const handleFilterData = (selectedRows, displayData) => {
    console.log("Selected Rows:", selectedRows);
    /*
    {
    "lookup": {
        "0": true,
        "1": true,
        "4": true,
        "5": true
    },
    "data": [
        {
            "index": 1,
            "dataIndex": 1
        },
        {
            "index": 0,
            "dataIndex": 0
        },
        {
            "index": 5,
            "dataIndex": 5
        },
        {
            "index": 4,
            "dataIndex": 4
        }
    ]
}
    
    */
    let selectedData = [];
    selectedRows.data.forEach(element => {
      selectedData.push(displayData[element.dataIndex].data);
    });
    setData(selectedData);
    setInvoicesToSend(selectedData);
  };

  const handleSendInvoices = async () => {
    setSendingInvoices(true);
    const token = localStorage.getItem("userToken");
    const invoiceIds = invoicesToSend.map(invoice => invoice[0]);
    await axios
      .post(
        api.invoices().retryAll(),
        { ids: invoiceIds },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      )
      .then(res => {
        const data = res.data;
        if (data.filter(i => i.success == false).length > 0) {
          setSnackBar({
            open: true,
            message:
              "Algunas facturas no se enviaron correctamente. Revisar columna error",
            severity: "warning"
          });
          setSendingInvoices(false);
          return;
        } else {
          setSnackBar({
            open: true,
            message: "Facturas enviadas correctamente",
            severity: "success"
          });
          setSendingInvoices(false);
        }
        setInvoicesToSend([]);
        fetchData(page, filters, orderColumn, orderOrder, rowsPerPage);
        fetchStatus();
      })
      .catch(err => {
        setSnackBar({
          open: true,
          message: "Error al enviar las facturas",
          severity: "error"
        });
        setSendingInvoices(false);
        console.error(err);
      });
  };

  let options = {
    serverSide: true,
    count: count,
    page: page,
    rowsPerPageOptions: [10, 25, 50, 100, rowsPerPage],
    onChangePage: setPage,
    onChangeRowsPerPage: number => setRowsPerPage(number),
    onColumnSortChange: (column, direction) => {
      setOrderColumn(column);
      setOrderOrder(direction);
    },
    customToolbar: () => {
      return (
        <Tooltip title={"Nuevo"}>
          <IconButton
            onClick={e => {
              window.location.href = "invoice/0";
            }}
          >
            <AddIcon />
          </IconButton>
        </Tooltip>
      );
    },
    customToolbarSelect: (selectedRows, displayData, setSelectedRows) => {
      return (
        <>
          {invoicesToSend.length == 0 && (
            <Tooltip title={"Filtrar selección"}>
              <IconButton
                onClick={e => {
                  handleFilterData(selectedRows, displayData);
                  setSelectedRows([]);
                }}
              >
                <FilterIcon />
              </IconButton>
            </Tooltip>
          )}
          {invoicesToSend.length > 0 && (
            <Tooltip title={"Enviar facturas seleccionadas"}>
              <IconButton
                disabled={sendingInvoices}
                onClick={e => {
                  handleSendInvoices();
                }}
                onDoubleClick={e => {
                  console.log("invoicesToSend", invoicesToSend);
                }}
              >
                <InvoiceIcon />
              </IconButton>
            </Tooltip>
          )}
        </>
      );
    },
    customFilterDialogFooter: filterList => {
      return (
        <div style={{ marginTop: "40px" }}>
          <Button
            variant="contained"
            onClick={() => {
              setPage(0);
              setFilters(filterList);
            }}
          >
            Aplicar
          </Button>
        </div>
      );
    },

    textLabels: {
      body: {
        noMatch: isLoading ? (
          <CircularProgress size={24} />
        ) : (
          "No hay datos disponibles"
        )
      }
    }
  };
  if (!isLoading) options.rowsPerPage = rowsPerPage;
  return (
    <>
      <Snackbar
        open={snackBar.open}
        onClose={value => setSnackBar({ ...snackBar, open: false })}
      >
        <Alert
          onClose={value => setSnackBar({ ...snackBar, open: false })}
          severity={snackBar.severity}
        >
          {snackBar.message}
        </Alert>
      </Snackbar>
      {(statusLoading || statusIndicators.length > 0) && (
        <div
          style={{
            display: "grid",
            gridTemplateColumns:
              "repeat(auto-fit, minmax(150px, 1fr))",
            gap: 12,
            marginBottom: 16
          }}
        >
          {statusLoading && statusIndicators.length === 0
            ? [1, 2, 3, 4].map(idx => (
                <div
                  key={idx}
                  style={{
                    padding: "12px 14px",
                    borderRadius: 6,
                    background: "#f5f7f9",
                    border: "1px solid #e6e9ed",
                    minHeight: 72
                  }}
                >
                  <LinearProgress />
                </div>
              ))
            : statusIndicators.map(item => {
                const style = getStatusStyle(item.value);
                const StatusIcon = style.Icon;
                const isActive =
                  filters[6] &&
                  filters[6].length === 1 &&
                  String(filters[6][0]) === String(item.value);
                const isZero = !item.count;

                return (
                  <button
                    key={item.value}
                    type="button"
                    onClick={() => handleStatusFilter(item.value)}
                    title={
                      item.subtitle
                        ? item.title + " — " + item.subtitle
                        : "Filtrar por " + item.title
                    }
                    style={{
                      display: "block",
                      width: "100%",
                      textAlign: "left",
                      padding: "12px 14px",
                      borderRadius: 6,
                      border: isActive
                        ? "1px solid " + style.color
                        : "1px solid " + style.border,
                      background: style.background,
                      cursor: "pointer",
                      outline: "none",
                      boxShadow: isActive
                        ? "inset 0 0 0 1px " + style.color
                        : "none",
                      opacity: isZero && !isActive ? 0.72 : 1
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        marginBottom: 6
                      }}
                    >
                      <span
                        style={{
                          fontSize: 22,
                          fontWeight: 700,
                          lineHeight: 1,
                          color: isZero ? "#90a4ae" : style.color
                        }}
                      >
                        {item.count}
                      </span>
                      <StatusIcon
                        style={{
                          fontSize: 20,
                          color: style.color,
                          opacity: isZero ? 0.45 : 0.9
                        }}
                      />
                    </div>
                    <div
                      style={{
                        fontSize: 13,
                        fontWeight: 600,
                        color: "#37474f",
                        lineHeight: 1.25,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis"
                      }}
                    >
                      {item.title}
                    </div>
                    {item.subtitle ? (
                      <div
                        style={{
                          marginTop: 2,
                          fontSize: 11,
                          color: "#78909c",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis"
                        }}
                      >
                        {item.subtitle}
                      </div>
                    ) : null}
                  </button>
                );
              })}
        </div>
      )}
      {isLoading ? (
        <div>
          <Typography variant="h6">Cargando...</Typography>
          <LinearProgress />
        </div>
      ) : (
        <MuiThemeProvider theme={getMuiTheme()}>
          <MUIDataTable
            title="Facturas"
            data={data}
            columns={columns}
            options={options}
          />
        </MuiThemeProvider>
      )}
      <Dialog
        open={pdfDialog.open}
        onClose={handleClosePdf}
        fullWidth
        maxWidth="xl"
        aria-labelledby="invoice-pdf-dialog-title"
      >
        <DialogTitle id="invoice-pdf-dialog-title">
          {pdfDialog.invoiceId
            ? `Factura #${pdfDialog.invoiceId}`
            : "Vista previa PDF"}
        </DialogTitle>
        <DialogContent style={{ minHeight: "70vh" }}>
          {pdfDialog.loading && (
            <div style={{ textAlign: "center", padding: "2rem" }}>
              <CircularProgress />
              <Typography variant="body2" style={{ marginTop: "1rem" }}>
                Cargando PDF...
              </Typography>
            </div>
          )}
          {!pdfDialog.loading && pdfDialog.url && (
            <embed
              src={`${pdfDialog.url}#toolbar=1&navpanes=0`}
              type="application/pdf"
              width="100%"
              height="700px"
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePdf} color="primary">
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default Invoices;
